import { OrderTypes } from "@shared/features/trades/common/types";
import {
  BotFormType,
  InitializeDCAStrategyValues,
  StrategyTabs,
} from "@shared/features/bots/BotForm/common/types";
import { useContext, useEffect } from "react";
import {
  BotFormContext,
  PreserveBotFormValuesContext,
} from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import {
  BotType,
  BuiltInStrategyData,
  ConfigurationItem,
  CustomStrategyData,
  StrategyStyleType,
} from "@shared/types";
import {
  getItemValue,
  getItemValueType,
} from "@shared/features/configuration/utils";
import { toFixed } from "@shared/utils/numbers";
import { channelValue } from "@shared/features/bots/BotForm/common/constants";
import { BotBaseStrategiesContext } from "@shared/features/bots/BotForm/Strategies/BotBaseStrategiesProvider";
import {
  isCustomStrategy,
  isDcaStrategy,
} from "@shared/features/bots/BotForm/common/utils";

interface useInitializeBotStrategiesProps {
  orderType: OrderTypes.ENTRY | OrderTypes.TAKE_PROFIT;
  tab?: StrategyTabs;
  selectedTab?: StrategyTabs;
  initialized: boolean;
  setInitialized: (initialized: boolean) => void;
  initializeDcaStrategyValues: InitializeDCAStrategyValues;
  initializeCustomStrategyValues: (
    targets?: CustomStrategyData["targets"]
  ) => void;
  createCustomStrategyTemplate?: ConfigurationItem;
  createDcaStrategyTemplate?: ConfigurationItem;
  templateStrategies?: ConfigurationItem[];
  setTemplateStrategy: (
    strategyType?: BuiltInStrategyData["builtin_strategy_type"]
  ) => void;
}

const useInitializeBotStrategies = ({
  orderType,
  tab,
  selectedTab,
  initialized,
  setInitialized,
  initializeDcaStrategyValues,
  initializeCustomStrategyValues,
  createCustomStrategyTemplate,
  createDcaStrategyTemplate,
  templateStrategies,
  setTemplateStrategy,
}: useInitializeBotStrategiesProps) => {
  const preserveValues = useContext(PreserveBotFormValuesContext);
  const {
    botType,
    botFormType,
    fieldsToInitialize,
    removeFieldToInitialize,
    entryStrategy,
    takeProfitStrategy,
    initialized: botFormInitialized,
    isBacktesting,
    isChannelConfiguration,
  } = useContext(BotFormContext) || {};
  const { initializeValues: initializeBaseValues } = useContext(
    BotBaseStrategiesContext
  )!;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isGridLikeBot = isGridBot || isHedgingBot;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isCreate =
    !botFormType ||
    botFormType === BotFormType.CREATE ||
    botFormType === BotFormType.BULK_CREATE;
  const isCustomStrategyTab = tab === StrategyTabs.CUSTOM;
  const isBuiltInStrategyTab = tab === StrategyTabs.BUILT_IN;
  const isDcaStrategyTab = tab === StrategyTabs.DCA;
  const isFocused = tab === selectedTab;
  const isEntry = orderType === OrderTypes.ENTRY;
  const strategy = isEntry ? entryStrategy : takeProfitStrategy;
  const parsedStrategy = strategy && !isSymbol(strategy) ? strategy : undefined;
  const fieldName = isEntry ? "entryStrategy" : "takeProfitStrategy";
  const shouldInitializeField = isFocused && fieldsToInitialize?.has(fieldName);
  const includeFirstOrderPercent =
    !isCustomStrategyTab && isEntry && !isGridLikeBot;

  const dcaStrategyTemplateValue =
    getItemValue(createDcaStrategyTemplate) || {};
  const customStrategyTemplateValue =
    getItemValue(createCustomStrategyTemplate) || {};

  const shouldInitializeCreate =
    isCreate &&
    !isGridLikeBot &&
    (createDcaStrategyTemplate || createCustomStrategyTemplate) &&
    !initialized &&
    !isBacktesting;
  const shouldInitializeEdit =
    (!isCreate || isBacktesting) && botFormInitialized && !initialized;

  useEffect(() => {
    let didInitialize = false;
    if (shouldInitializeEdit || shouldInitializeField) {
      didInitialize = true;
      initializeValuesFromFormStrategy();
    } else if (shouldInitializeCreate) {
      didInitialize = true;
      if (preserveValues && parsedStrategy) initializeValuesFromFormStrategy();
      else initializeValuesFromTemplate();
    }
    if (didInitialize && !initialized) setInitialized(true);
    if (shouldInitializeField) {
      removeFieldToInitialize?.(fieldName);
    }
  }, [
    shouldInitializeCreate,
    shouldInitializeEdit,
    shouldInitializeField,
    isCustomStrategyTab,
  ]);

  const setSpecificStrategyFieldsFromTemplate = () => {
    if (isBuiltInStrategyTab) {
      const defaultTemplateStrategy =
        parsedStrategy && "builtin_strategy_type" in parsedStrategy
          ? parsedStrategy?.builtin_strategy_type
          : undefined;
      setTemplateStrategy(
        defaultTemplateStrategy ??
          parseInt(getItemValueType(templateStrategies?.[0]))
      );
    } else if (isDcaStrategyTab) {
      const defaultAmountScale =
        parsedStrategy && "amount_scale" in parsedStrategy
          ? parsedStrategy?.amount_scale
          : undefined;
      let defaultFirstOrderPercent =
        includeFirstOrderPercent &&
        parsedStrategy &&
        "first_order_percentage" in parsedStrategy
          ? parsedStrategy?.first_order_percentage
          : undefined;
      if (defaultFirstOrderPercent && !isSymbol(defaultFirstOrderPercent)) {
        defaultFirstOrderPercent = toFixed(defaultFirstOrderPercent, 2);
      }
      initializeDcaStrategyValues(
        defaultAmountScale ?? dcaStrategyTemplateValue.amount_scale,
        defaultFirstOrderPercent
      );
    } else if (isCustomStrategyTab) {
      const defaultTargets =
        parsedStrategy && "targets" in parsedStrategy
          ? parsedStrategy?.targets
          : undefined;
      initializeCustomStrategyValues(
        defaultTargets ?? customStrategyTemplateValue.targets
      );
    }
  };
  const initializeValuesFromTemplate = () => {
    const defaultNumActiveOrders = isEntry
      ? dcaStrategyTemplateValue.number_of_active_orders
      : undefined;
    if (isFocused) {
      initializeBaseValues(
        isSignalsBot ? channelValue : dcaStrategyTemplateValue.number_of_orders,
        isSignalsBot
          ? channelValue
          : dcaStrategyTemplateValue.price_difference * 100,
        isSignalsBot ? channelValue : dcaStrategyTemplateValue.price_scale,
        isSignalsBot ? channelValue : defaultNumActiveOrders
      );
    }
    setSpecificStrategyFieldsFromTemplate();
  };

  const initializeValuesFromFormStrategy = () => {
    if (
      !strategy ||
      isSymbol(strategy) ||
      (!isSignalsBot && isSymbol(strategy?.strategy_style))
    ) {
      initializeValuesFromTemplate();
      return;
    }

    const isCurrentBuiltInStrategy =
      isSymbol(strategy?.strategy_style) ||
      strategy?.strategy_style === StrategyStyleType.BUILT_IN;
    const isCurrentDcaStrategy = isDcaStrategy(strategy);
    const isCurrentCustomStrategy = isCustomStrategy(strategy);
    if (isFocused) {
      const defaultNumOrders =
        "number_of_orders" in strategy ? strategy.number_of_orders : undefined;
      initializeBaseValues(
        defaultNumOrders ??
          (isChannelConfiguration
            ? undefined
            : dcaStrategyTemplateValue.number_of_orders),
        strategy.price_difference,
        strategy.price_scale,
        strategy.number_of_active_orders ?? undefined
      );
      if (isCurrentBuiltInStrategy) {
        setTemplateStrategy(
          "builtin_strategy_type" in strategy
            ? strategy.builtin_strategy_type
            : undefined
        );
      } else if (isCurrentDcaStrategy) {
        initializeDcaStrategyValues(
          strategy.amount_scale ?? dcaStrategyTemplateValue.amount_scale,
          strategy.first_order_percentage,
          strategy.baseline
        );
      } else if (isCurrentCustomStrategy) {
        initializeCustomStrategyValues(strategy.targets);
      }
    } else {
      setSpecificStrategyFieldsFromTemplate();
    }
  };
};

export default useInitializeBotStrategies;
